"use client";

import type { NavActionCtaData } from "@homeinstead/shared/site-builder";

import { cn } from "../../utils/classnames";
import { NavActionCta } from "./NavActionCta";

type NavBarCTAProps = {
  phoneNumber: string;
  primary: NavActionCtaData | null;
  scrolledPastViewport: boolean;
  secondaryCta: NavActionCtaData | null;
  hidePrimaryCta?: boolean;
};

/**
 * Renders the right-side CTA stack in the NavBar header.
 *
 * Larger viewports (sm and up):
 *   - `secondaryCta` (when configured) renders to the left of primary;
 *     uses the primitive's authored color variant (default new-beginnings →
 *     dark "primary" button styling, matching today's consultation CTA).
 *   - `primary` (unless `hidePrimaryCta`) renders to the right with friendly
 *     yellow styling — matching today's primary phone/link CTA.
 *
 * Small viewports (below sm):
 *   - Primary renders inline next to the logo with friendly styling.
 *   - The secondary header slot is hidden on mobile; the matched-or-default
 *     secondary variant renders in the bottom-sticky panel instead (handled
 *     by NavigationBottomPanel on the NavBar layout).
 */
function NavBarCTA({
  hidePrimaryCta = false,
  phoneNumber,
  primary,
  scrolledPastViewport,
  secondaryCta,
}: NavBarCTAProps) {
  const renderSecondary = secondaryCta !== null;
  const renderPrimary = primary !== null && !hidePrimaryCta;
  const shouldRenderLargeViewportCtas = renderSecondary || renderPrimary;

  // Matches the existing fade-in animation on the desktop secondary slot —
  // the consultation CTA today appears once the visitor has scrolled past one
  // viewport height. Preserved 1:1 so editors who didn't author scroll-hide
  // see no behavior change.
  //
  // Always use outline-light (not Link `secondary`): that variant embeds
  // `hidden md:inline-flex`, which forces the CTA into the layout on desktop
  // and breaks the opacity/width scroll gate below. Campaign keeps the same
  // variant and only recolors for the white shell via group-data on `<nav>`.
  const desktopSecondaryClassName = cn(
    "translate-x-0 sm:hidden",
    scrolledPastViewport
      ? "w-full motion-safe:animate-fade-in-right md:inline-flex"
      : "w-0 translate-x-8 opacity-0",
    "group-data-[appearance=campaign]/navbar:border-new-beginnings-base group-data-[appearance=campaign]/navbar:bg-background group-data-[appearance=campaign]/navbar:text-new-beginnings-base group-data-[appearance=campaign]/navbar:focus:border-new-beginnings-base group-data-[appearance=campaign]/navbar:focus:outline-new-beginnings-base group-data-[appearance=campaign]/navbar:hover:bg-new-beginnings-tints-800 group-data-[appearance=campaign]/navbar:hover:text-new-beginnings-base",
  );

  return (
    <>
      {/* Large viewport CTAs (sm and up). */}
      {shouldRenderLargeViewportCtas ? (
        <div className="hidden h-12 items-center gap-3 sm:flex xl:me-0">
          {renderSecondary ? (
            <NavActionCta
              className={desktopSecondaryClassName}
              cta={secondaryCta}
              phoneNumber={phoneNumber}
              variant="outline-light"
            />
          ) : null}
          {renderPrimary ? (
            <NavActionCta
              className="z-10"
              cta={primary}
              phoneNumber={phoneNumber}
              variant="friendly"
            />
          ) : null}
        </div>
      ) : null}

      {/* Small viewport primary CTA (below sm). The small viewport secondary slot lives in the
          NavigationBottomPanel rendered separately by NavBar. */}
      {renderPrimary ? (
        <NavActionCta
          className="flex transition-all duration-300 ease-in-out sm:hidden"
          cta={primary}
          phoneNumber={phoneNumber}
          variant="friendly"
        />
      ) : null}
    </>
  );
}

export { NavBarCTA };
